import { useCallback, useEffect, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { DataStateRow } from "@/components/ui/data-state";
import { toast } from "sonner";
import { RefreshCcw, CheckCircle, XCircle, Clock } from "lucide-react";

const STATUS_LABELS = {
  pending:   { label: "Chờ duyệt",    color: "bg-amber-50 text-amber-700" },
  approved:  { label: "Đã duyệt",     color: "bg-green-50 text-green-700" },
  rejected:  { label: "Từ chối",      color: "bg-red-50 text-red-600" },
  cancelled: { label: "Đã huỷ",       color: "bg-stone-100 text-stone-500" },
  fulfilled: { label: "Đã cấp phát",  color: "bg-blue-50 text-blue-700" },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || { label: status, color: "bg-stone-100 text-stone-600" };
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${s.color}`}>{s.label}</span>
  );
}

// ---------------------------------------------------------------------------
// Approve dialog — custodian selects which document to link per item
// ---------------------------------------------------------------------------
function ApproveDialog({ open, onClose, onDone, request }) {
  const [documents, setDocuments] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});   // document_type_id → doc_id
  const [ttlHours, setTtlHours] = useState("72");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !request) return;
    // Load request items and available documents for entity
    Promise.all([
      api.get(`/legal/document-types?entity_id=${request.entity_id}`),
      api.get(`/legal/documents?entity_id=${request.entity_id}`),
    ]).then(([dtRes, docRes]) => {
      setDocuments(docRes.data || []);
    }).catch(() => {});
    setSelected({});
    setTtlHours("72");
  }, [open, request]);

  const submit = async (e) => {
    e.preventDefault();
    const docIds = Object.values(selected).filter(Boolean);
    if (!docIds.length) { toast.error("Chọn ít nhất 1 tài liệu"); return; }
    setSaving(true);
    try {
      await api.post(`/legal/requests/${request.id}/approve`, {
        document_ids: docIds,
        ttl_hours: parseInt(ttlHours, 10),
      });
      toast.success("Đã duyệt yêu cầu và cấp quyền truy cập");
      onDone();
      onClose();
    } catch (err) {
      toast.error(parseApiError(err, "Không duyệt được").message);
    } finally {
      setSaving(false);
    }
  };

  const activeDocuments = documents.filter((d) => d.status === "active");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Duyệt yêu cầu hồ sơ</DialogTitle>
        </DialogHeader>
        {request && (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="bg-stone-50 rounded p-3 text-sm space-y-1">
              <div><span className="text-stone-500">Người yêu cầu:</span> {request.requester_name}</div>
              <div><span className="text-stone-500">Pháp nhân:</span> {request.entity_name}</div>
              <div><span className="text-stone-500">Mục đích:</span> {request.purpose}</div>
            </div>

            <div>
              <Label>Chọn tài liệu để cấp phát</Label>
              {activeDocuments.length === 0
                ? <p className="text-sm text-stone-400 mt-1">Không có tài liệu active cho pháp nhân này</p>
                : activeDocuments.map((doc) => (
                  <label key={doc.id} className="flex items-center gap-2 mt-1.5 text-sm cursor-pointer">
                    <input type="checkbox"
                      checked={!!Object.values(selected).find((v) => v === doc.id)}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = { ...prev };
                          if (e.target.checked) next[doc.id] = doc.id;
                          else delete next[doc.id];
                          return next;
                        });
                      }}
                    />
                    <span>{doc.file_name} — v{doc.version_no}</span>
                  </label>
                ))
              }
            </div>

            <div>
              <Label>TTL (giờ)</Label>
              <Select value={ttlHours} onValueChange={setTtlHours}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 giờ</SelectItem>
                  <SelectItem value="48">48 giờ</SelectItem>
                  <SelectItem value="72">72 giờ (mặc định)</SelectItem>
                  <SelectItem value="168">7 ngày</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Huỷ</Button>
              <Button type="submit" disabled={saving || activeDocuments.length === 0}
                data-testid="legal-request-approve-confirm">
                {saving ? "Đang duyệt…" : "Xác nhận duyệt"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Reject dialog
// ---------------------------------------------------------------------------
function RejectDialog({ open, onClose, onDone, request }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error("Nhập lý do từ chối"); return; }
    setSaving(true);
    try {
      await api.post(`/legal/requests/${request.id}/reject`, { reason: reason.trim() });
      toast.success("Đã từ chối yêu cầu");
      onDone();
      onClose();
    } catch (err) {
      toast.error(parseApiError(err, "Không từ chối được").message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Từ chối yêu cầu</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label>Lý do từ chối</Label>
            <Textarea required rows={3} value={reason}
              onChange={(e) => setReason(e.target.value)} placeholder="Nhập lý do…" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Huỷ</Button>
            <Button type="submit" variant="destructive" disabled={saving}>{saving ? "Đang lưu…" : "Từ chối"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function LegalVaultRequests() {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, request: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const r = await api.get(`/legal/requests${params}`);
      setRequests(r.data || []);
    } catch (e) {
      setError(parseApiError(e, "Không tải được danh sách yêu cầu").message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="page-legal-requests">
      <PageHeader
        kicker="Admin • Phê duyệt hồ sơ"
        title="Yêu cầu cấp tài liệu pháp lý"
        breadcrumb={["Pháp lý", "Phê duyệt yêu cầu"]}
        actions={
          <Button type="button" variant="outline" onClick={load} className="rounded-sm">
            <RefreshCcw className="w-4 h-4 mr-1.5" />Tải lại
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {["", "pending", "approved", "rejected", "cancelled"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${statusFilter === s ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 hover:border-stone-400"}`}
            >
              {s ? STATUS_LABELS[s]?.label || s : "Tất cả"}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          {loading && <DataStateRow loading />}
          {error && <div className="p-4 text-red-600 text-sm">{error}</div>}
          {!loading && !error && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 text-stone-500 text-xs bg-stone-50">
                  <th className="px-4 py-2 text-left">Người yêu cầu</th>
                  <th className="px-4 py-2 text-left">Pháp nhân</th>
                  <th className="px-4 py-2 text-left">Mục đích</th>
                  <th className="px-4 py-2 text-left">Ngày tạo</th>
                  <th className="px-4 py-2 text-left">Trạng thái</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">Không có yêu cầu nào</td></tr>
                )}
                {requests.map((req) => (
                  <tr key={req.id} className="border-b border-stone-50 hover:bg-stone-50"
                    data-testid={`legal-request-row-${req.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{req.requester_name}</div>
                      <div className="text-xs text-stone-400">{req.requester_email}</div>
                    </td>
                    <td className="px-4 py-3">{req.entity_name}</td>
                    <td className="px-4 py-3 max-w-[200px]">
                      <p className="truncate">{req.purpose}</p>
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">
                      {new Date(req.created_at).toLocaleDateString("vi-VN")}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={req.status} />
                    </td>
                    <td className="px-4 py-3 text-right space-x-1.5">
                      {req.status === "pending" && (
                        <>
                          <Button size="sm" onClick={() => setApproveDialog({ open: true, request: req })}
                            data-testid={`legal-request-approve-${req.id}`}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" />Duyệt
                          </Button>
                          <Button size="sm" variant="outline"
                            onClick={() => setRejectDialog({ open: true, request: req })}
                            data-testid={`legal-request-reject-${req.id}`}>
                            <XCircle className="w-3.5 h-3.5 mr-1" />Từ chối
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ApproveDialog
        open={approveDialog.open}
        onClose={() => setApproveDialog({ open: false, request: null })}
        onDone={load}
        request={approveDialog.request}
      />
      <RejectDialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, request: null })}
        onDone={load}
        request={rejectDialog.request}
      />
    </div>
  );
}
